import { useState } from "react";
import axios from "axios";
import { Linkedin, Mail } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { Reveal, Eyebrow, PageHero } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-[#D4D8E0] bg-white px-4 py-3 text-sm text-[#1A1A1A] outline-none transition-colors placeholder:text-[#98A0B3] focus:border-[#B08D3F]";

export default function Contact() {
  useSeo(
    "Contact | Reform Intelligence®",
    "Request a briefing from Reform Intelligence, or start a conversation about independent AI evaluation and governance."
  );

  const [form, setForm] = useState({ name: "", organization: "", role: "", email: "", message: "", website: "" });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(false);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.website) { setSent(true); return; }
    setSending(true);
    try {
      await axios.post(`${API}/leads`, form);
      setSent(true);
    } catch {
      setError(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Contact"
        headline="Start a conversation."
        subhead="Tell us about your organization and what you are trying to govern. We will follow up to arrange a briefing."
      />

      <section className="bg-white">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-16 px-5 py-20 sm:px-8 sm:py-24 lg:grid-cols-[1.3fr_1fr]">
          <Reveal>
            {sent ? (
              <div data-testid="briefing-confirmation" className="border border-[#B08D3F]/40 bg-[#F5F6F8] p-10">
                <Eyebrow>Request received</Eyebrow>
                <h2 className="mt-4 font-serif2 text-2xl font-semibold text-[#1F2D4E]">Thank you.</h2>
                <p className="mt-4 leading-relaxed text-[#5A6B8C]">
                  Your briefing request has been received. A member of Reform Intelligence will follow up with you directly.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="briefing-form" className="border border-[#E4E7EC] bg-[#F5F6F8] p-8 sm:p-10">
                <h2 className="font-serif2 text-2xl font-semibold text-[#1F2D4E]">Request a briefing</h2>
                <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="bf-name" className="mb-2 block text-xs font-semibold uppercase tracking-[0.12em] text-[#5A6B8C]">Full name *</label>
                    <input id="bf-name" data-testid="briefing-name" required value={form.name} onChange={set("name")} className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="bf-org" className="mb-2 block text-xs font-semibold uppercase tracking-[0.12em] text-[#5A6B8C]">Organization *</label>
                    <input id="bf-org" data-testid="briefing-organization" required value={form.organization} onChange={set("organization")} className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="bf-role" className="mb-2 block text-xs font-semibold uppercase tracking-[0.12em] text-[#5A6B8C]">Role or title</label>
                    <input id="bf-role" data-testid="briefing-role" value={form.role} onChange={set("role")} className={inputCls} />
                  </div>
                  <div>
                    <label htmlFor="bf-email" className="mb-2 block text-xs font-semibold uppercase tracking-[0.12em] text-[#5A6B8C]">Email *</label>
                    <input id="bf-email" data-testid="briefing-email" required type="email" value={form.email} onChange={set("email")} className={inputCls} />
                  </div>
                </div>
                <div className="mt-5">
                  <label htmlFor="bf-message" className="mb-2 block text-xs font-semibold uppercase tracking-[0.12em] text-[#5A6B8C]">Message</label>
                  <textarea id="bf-message" data-testid="briefing-message" rows={5} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} />
                </div>
                <input
                  type="text"
                  name="website"
                  value={form.website}
                  onChange={set("website")}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="absolute -left-[9999px] h-0 w-0 opacity-0"
                />
                <button
                  type="submit"
                  data-testid="briefing-submit"
                  disabled={sending}
                  className="mt-8 bg-[#1F2D4E] px-8 py-3.5 text-sm font-semibold uppercase tracking-[0.12em] text-white transition-colors hover:bg-[#B08D3F] disabled:opacity-60"
                >
                  {sending ? "Sending…" : "Submit request"}
                </button>
                {error && <p data-testid="briefing-error" className="mt-4 text-sm text-red-600">Something went wrong. Please try again.</p>}
              </form>
            )}
          </Reveal>

          <Reveal delay={0.1}>
            <div>
              <Eyebrow>Other ways to reach us</Eyebrow>
              <div className="mt-6 space-y-5">
                <a href="mailto:contact@reformintelligence.com" data-testid="contact-email" className="flex items-center gap-3 text-[#1F2D4E] transition-colors hover:text-[#B08D3F]">
                  <Mail size={18} className="text-[#B08D3F]" />
                  <span className="text-sm font-medium">contact@reformintelligence.com</span>
                </a>
                <a
                  href="https://www.linkedin.com/company/reform-intelligence-ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-linkedin"
                  className="flex items-center gap-3 text-[#1F2D4E] transition-colors hover:text-[#B08D3F]"
                >
                  <Linkedin size={18} className="text-[#B08D3F]" />
                  <span className="text-sm font-medium">linkedin.com/company/reform-intelligence-ai</span>
                </a>
              </div>
              <div className="mt-10 border-t border-[#E4E7EC] pt-8">
                <p className="text-sm leading-relaxed text-[#5A6B8C]">
                  Reform Intelligence engages with institutional leadership, including chief risk officers, chief information officers, general counsel, and board audit committees.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
